/**
 * 音声読み上げ（Text-to-Speech）カスタムフック
 *
 * Web Speech API（SpeechSynthesis）を使用してテキストを読み上げる
 */

import { useState, useEffect, useCallback } from 'react';

/**
 * 音声読み上げフック
 *
 * @returns {Object} 音声読み上げ関連の状態と関数
 */
export function useSpeech() {
  // ブラウザが音声合成をサポートしているか
  const [isSupported, setIsSupported] = useState(false);
  // 現在読み上げ中かどうか
  const [isSpeaking, setIsSpeaking] = useState(false);
  // 現在読み上げ中のテキストID
  const [currentSpeakingId, setCurrentSpeakingId] = useState(null);

  // ブラウザサポートチェック
  useEffect(() => {
    setIsSupported('speechSynthesis' in window);
  }, []);

  /**
   * テキストの言語を検出
   * 日本語が含まれていれば ja-JP、そうでなければ en-US
   *
   * @param {string} text - 検出対象のテキスト
   * @returns {string} 言語コード
   */
  const detectLanguage = useCallback((text) => {
    // 日本語文字（ひらがな、カタカナ、漢字）が含まれているかチェック
    const japaneseRegex = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/;
    return japaneseRegex.test(text) ? 'ja-JP' : 'en-US';
  }, []);

  /**
   * テキストを読み上げ用に変換（固有名詞の読み方を修正）
   *
   * @param {string} text - 元のテキスト
   * @returns {string} 読み上げ用に変換されたテキスト
   */
  const convertForSpeech = useCallback((text) => {
    // Thoth -> トート（日本語読み）
    return text.replace(/Thoth/gi, 'トート');
  }, []);

  /**
   * テキストを読み上げる
   *
   * @param {string} text - 読み上げるテキスト
   * @param {string} id - テキストを識別するID（オプション）
   */
  const speak = useCallback((text, id = null) => {
    if (!isSupported || !text) return;

    // 現在読み上げ中なら停止
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      // 同じIDの場合は停止のみ（トグル動作）
      if (id && id === currentSpeakingId) {
        setIsSpeaking(false);
        setCurrentSpeakingId(null);
        return;
      }
    }

    // 読み上げ用にテキストを変換
    const speechText = convertForSpeech(text);

    // 新しい発話を作成
    const utterance = new SpeechSynthesisUtterance(speechText);
    utterance.lang = detectLanguage(text);
    utterance.rate = 1.0; // 速度（0.1〜10）
    utterance.pitch = 1.0; // ピッチ（0〜2）
    utterance.volume = 1.0; // 音量（0〜1）

    // イベントハンドラ
    utterance.onstart = () => {
      setIsSpeaking(true);
      setCurrentSpeakingId(id);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setCurrentSpeakingId(null);
    };

    utterance.onerror = (event) => {
      console.error('[KnoVelo Speech] 読み上げエラー:', event.error);
      setIsSpeaking(false);
      setCurrentSpeakingId(null);
    };

    // 読み上げ開始
    window.speechSynthesis.speak(utterance);
  }, [isSupported, isSpeaking, currentSpeakingId, detectLanguage, convertForSpeech]);

  /**
   * 読み上げを停止
   */
  const stop = useCallback(() => {
    if (!isSupported) return;
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    setCurrentSpeakingId(null);
  }, [isSupported]);

  /**
   * 特定のIDが現在読み上げ中かどうか
   *
   * @param {string} id - チェックするID
   * @returns {boolean} 読み上げ中かどうか
   */
  const isSpeakingId = useCallback((id) => {
    return isSpeaking && currentSpeakingId === id;
  }, [isSpeaking, currentSpeakingId]);

  return {
    isSupported,
    isSpeaking,
    speak,
    stop,
    isSpeakingId
  };
}

export default useSpeech;
